import { useTranslation } from 'react-i18next';
import { useLanguage, type Language } from '../contexts/LanguageContext';

/** BCP 47 locale per UI language, for Intl formatting. */
const LOCALE: Partial<Record<Language, string>> = {
  fi: 'fi-FI',
  sv: 'sv-SE',
  da: 'da-DK',
  de: 'de-DE',
  pl: 'pl-PL',
};
const localeFor = (lang: Language) => LOCALE[lang] ?? 'en-US';

/**
 * Currency per market. Sweden and Denmark keep their own krona/krone rather
 * than the euro; only the euro-area locales get EUR.
 */
const CURRENCY: Partial<Record<Language, string>> = {
  fi: 'EUR',
  de: 'EUR',
  sv: 'SEK',
  da: 'DKK',
  pl: 'PLN',
};

/**
 * Custom hook that combines react-i18next with our language context
 * Provides a clean interface for accessing translations and language state
 */
export const useTranslations = () => {
  const { t, i18n } = useTranslation();
  const { currentLanguage, changeLanguage, isLoading } = useLanguage();

  return {
    // Translation function
    t,
    
    // Language state
    currentLanguage,
    changeLanguage,
    isLoading,
    
    // Additional helpers
    isReady: i18n.isInitialized,
    
    // Format helpers for common use cases
    formatters: {
      // Format currency based on current language
      currency: (amount: number) => {
        return new Intl.NumberFormat(localeFor(currentLanguage), {
          style: 'currency',
          currency: CURRENCY[currentLanguage] ?? 'USD',
        }).format(amount);
      },

      // Format dates based on current language
      date: (date: Date) => {
        return new Intl.DateTimeFormat(localeFor(currentLanguage), {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }).format(date);
      },

      // Format numbers based on current language
      number: (number: number) => {
        return new Intl.NumberFormat(localeFor(currentLanguage)).format(number);
      },
    },
  };
};

export default useTranslations; 